import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { ADMIN_PANEL, NO_ACCESS, SALES_EDGE_SEATS } from '@/constants/team';
import { initStore } from '@/stores/store-utils';
import { SalesEdgeRole } from '@/types/root';
import { Invite, TeamAccessAuthorization, TeamUser } from '@/types/team';

type RoleWithNoAccess = SalesEdgeRole | typeof NO_ACCESS | null;
const CORE = 'core';
const SALES = 'sales';

export const useTeamStore = defineStore('TeamStore', () => {
  const authorizations = ref<TeamAccessAuthorization[]>([]);
  const redeemableInvites = ref<Invite[]>([]);
  const revokedUsers = ref<TeamUser[]>([]);

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const { data } = await crossbeamApi.GET('/v0.1/team');
      if (!data) return;

      authorizations.value = data.authorizations;
      redeemableInvites.value = data.redeemable_invites;
      revokedUsers.value = data.revoked_users;
    },
  );

  refresh({ useCache: true });

  /* For both core and sales:
   * - An "invite" is an invitation that hasn't been redeemed yet
   * - A "user" is an authorization, it's what's used by an actual person when using Crossbeam
   * - A "seat" is either of these things (it's used to count an organization's total number of seats occupied, for instance)
   */

  const coreInvites = computed(() => {
    return redeemableInvites.value.filter(
      (invite) => invite.seat_type === CORE,
    );
  });

  const coreUsers = computed(() => {
    return authorizations.value.filter((auth) => {
      return auth.seat_type === CORE && auth.authorizer_type !== ADMIN_PANEL;
    });
  });

  const coreSeats = computed<(Invite | TeamAccessAuthorization)[]>(() => [
    ...coreUsers.value,
    ...coreInvites.value,
  ]);

  const salesInvites = computed(() =>
    redeemableInvites.value.filter((invite) => {
      return (
        invite.sales_edge_role &&
        SALES_EDGE_SEATS.includes(invite.sales_edge_role)
      );
    }),
  );
  const salesUsers = computed(() => {
    return authorizations.value.filter((auth) => {
      return (
        auth.authorizer_type !== ADMIN_PANEL &&
        auth.sales_edge_role &&
        SALES_EDGE_SEATS.includes(auth.sales_edge_role)
      );
    });
  });
  const salesSeats = computed<(Invite | TeamAccessAuthorization)[]>(() => [
    ...salesUsers.value,
    ...salesInvites.value,
  ]);
  const salesOnlyUsers = computed<TeamAccessAuthorization[]>(() => {
    return salesUsers.value.filter((auth) => {
      return auth.seat_type === SALES && auth.authorizer_type !== ADMIN_PANEL;
    });
  });

  const salesOnlySeats = computed<(Invite | TeamAccessAuthorization)[]>(() => [
    ...salesOnlyUsers.value,
    ...salesInvites.value,
  ]);

  const allSeats = computed(() => {
    const seen =
      new Set(); /* We need to get every unique instance of an email or invite, including the login type (since you can have SSO + Normal */
    const invites = [];
    for (const authOrInvite of [...salesSeats.value, ...coreSeats.value]) {
      const emailWithLogin =
        'email' in authOrInvite
          ? authOrInvite.email
          : `${authOrInvite.user.email}__${authOrInvite.login_method}`;
      seen.add(emailWithLogin);
      invites.push(authOrInvite);
    }
    return invites;
  });

  // TODO: For new onboarding
  const hasCrmAdmin = computed(() => {
    return false;
  });

  function getAuthorizationByUserId(id?: number) {
    return authorizations.value.find(
      (authorization) => authorization.user.id === id,
    );
  }

  function getUserById(id: number) {
    const auth = authorizations.value.find((a) => a.user.id === id);
    const revokedAuth = revokedUsers.value.find((u) => u.id === id);
    return auth
      ? auth.user
      : revokedAuth
        ? {
            ...revokedAuth,
            last_name: `${revokedAuth.last_name} (Deactivated)`,
          }
        : { first_name: 'Admin', last_name: '' };
  }

  function getAuthsByRoleId(roleId: string) {
    return authorizations.value.filter(
      (authorization) => authorization.role_id === roleId,
    );
  }

  function normalizeNoAccessRole(role: RoleWithNoAccess): SalesEdgeRole | null {
    return !role || role === NO_ACCESS ? null : role;
  }

  function getUserName(userId?: number) {
    if (!userId) return 'Unknown user';

    const user = getAuthorizationByUserId(userId)?.user;
    if (!user) return 'Unknown user';

    const { first_name, last_name } = user;

    if (first_name && last_name) {
      return `${first_name} ${last_name}`;
    }

    if (first_name) {
      return first_name;
    }

    return 'Unknown user';
  }

  async function updateRoles({
    userIds,
    salesEdgeRoleId,
    crossbeamRoleId,
  }: {
    userIds: number[];
    salesEdgeRoleId: RoleWithNoAccess;
    crossbeamRoleId: RoleWithNoAccess;
  }) {
    const updatedRoles = userIds.map((userId) => ({
      user_id: userId,
      role_id: normalizeNoAccessRole(crossbeamRoleId),
      sales_edge_role: normalizeNoAccessRole(salesEdgeRoleId),
    }));
    const { error } = await crossbeamApi.PUT('/v0.1/team/roles', {
      body: updatedRoles,
    });
    if (error) throw error;
    await refresh();
  }

  async function sendInvites({
    emails,
    salesEdgeRoleId,
    crossbeamRoleId,
    isSSO,
    onBehalfOf,
  }: {
    emails: string[];
    crossbeamRoleId: RoleWithNoAccess;
    salesEdgeRoleId: RoleWithNoAccess;
    isSSO: boolean;
    onBehalfOf?: null | number;
  }) {
    const invites = emails.map((email) => ({
      email,
      role_id: normalizeNoAccessRole(crossbeamRoleId),
      sales_edge_role: normalizeNoAccessRole(salesEdgeRoleId),
      purpose: 'user',
      is_sso: isSSO,
    }));

    const { error } = await crossbeamApi.POST('/v0.2/user-invites', {
      body: { invites, on_behalf_of: onBehalfOf },
    });
    if (error) throw error;

    return invites;
  }

  async function removeAuthorization({ userId }: { userId: number }) {
    const { error } = await crossbeamApi.POST(
      '/v0.1/team/{user_id}/deauthorize',
      {
        params: { path: { user_id: userId } },
      },
    );
    if (error) throw error;
    authorizations.value = authorizations.value.filter(
      (authorization) => authorization.user.id !== userId,
    );
  }
  async function revokeInvite({ inviteId }: { inviteId: number }) {
    const { error } = await crossbeamApi.DELETE(
      '/v0.1/delete-user-invites/{id}',
      {
        params: { path: { id: inviteId } },
      },
    );
    if (error) throw error;
    redeemableInvites.value = redeemableInvites.value.filter((invite) => {
      return invite.id !== inviteId;
    });
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    authorizations,
    redeemableInvites,
    revokedUsers,
    coreSeats,
    coreInvites,
    coreUsers,
    salesInvites,
    salesOnlySeats,
    salesUsers,
    salesSeats,
    salesOnlyUsers,
    allSeats,
    hasCrmAdmin,
    getUserName,
    getUserById,
    getAuthsByRoleId,
    getAuthorizationByUserId,
    refreshTeamStore: refresh,
    sendInvites,
    updateRoles,
    removeAuthorization,
    revokeInvite,
  };
});
